import { OnDestroy } from '@angular/core';
import { Injectable } from '@angular/core';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { Observable, of, Subject } from 'rxjs';
import { IdsPairingData, OAuthToken, IdsPairingStatus } from '@shared/generalInterfaces';
import { map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { Utils } from '@shared/utils.service';
import { WindowsApplicationService } from '../WindowsApplication/windows-application.service';
import { WindowsApplicationQuery } from 'app/settings/state/windows-application.query';
import { TimberService } from 'app/services/logging/timber.service';

@Injectable({
	providedIn: 'root',
})
export class DoctorPairingService implements OnDestroy {
	public iosimAuthenticationSuccess = new Subject<boolean>();
	pollingInterval: any;
	private cachedOAuthTokens = new Map<number, Observable<OAuthToken>>();
	constructor(
		private eupRoutesService: EupRoutesService,
		private eupHttpHandler: EupHttpHandler,
		private globalSettingsService: GlobalSettingsService,
		private utils: Utils,
		private windowsApplicationService: WindowsApplicationService,
		private windowsApplicationQuery: WindowsApplicationQuery,
		private readonly timberService: TimberService
	) {}

	getIdsPairingData(): Observable<IdsPairingData> {
		const url = this.eupRoutesService.idsPairing.getIdsPairingData();
		return this.eupHttpHandler.get(url, undefined, true, false);
	}

	isDoctorPaired(): Observable<boolean> {
		const url = this.eupRoutesService.idsPairing.getIsDoctorPaired();
		return this.eupHttpHandler.get(url, undefined, true, false).pipe(map((res) => res as boolean));
	}

	getIOSimLinkWithOAuth(companyId: number, orderId: number): Observable<string> {
		const url = this.eupRoutesService.idsPairing.getIOSimLinkWithOAuth(companyId, orderId);
		return this.eupHttpHandler.get(url, undefined, true, false);
	}

	openIdsLoginWindow(): void {
		const langCode = this.globalSettingsService.get().selectedLanguage.code;
		const companyId = this.globalSettingsService.get().selectedCompanyId;
		const url = this.eupRoutesService.idsPairing.getIdsLoginUrl(langCode, companyId);
		this.eupHttpHandler
			.get(url, { observe: 'response' })
			.pipe(
				map(({ body }) => this.openPopupWindow(body)),
				tap((loginWindow) => this.setPollingInterval(loginWindow))
			)
			.subscribe();
	}

	openPopupWindow(url: string): Window {
		const height = 600;
		const width = 800;
		const top = window.outerHeight / 2 + window.screenY - height / 2;
		const left = window.outerWidth / 2 + window.screenX - width / 2;

		return window.open(`${url}${this.windowsApplicationQuery.promptLogin ? '&prompt=login' : ''}`, 'iosim-login-page', `width=${width},height=${height},top=${top},left=${left}`);
	}

	getOAuthToken(): Observable<OAuthToken> {
		const companyId = this.globalSettingsService.get().selectedCompanyId;
		
		if (!this.cachedOAuthTokens.has(companyId)) {
			this.timberService.info(`[IOSim DoctorPairingService.getOAuthToken] Cache miss for companyId=${companyId}, fetching token.`, { module: 'DoctorPairingService' });
			const url = this.eupRoutesService.idsPairing.getOAuthToken(companyId);
			const cachedToken$ = this.eupHttpHandler.get(url, undefined, true, false).pipe(
				tap((token) => this.timberService.info(`[IOSim DoctorPairingService.getOAuthToken] Token fetched successfully for companyId=${companyId}, hasRefreshToken=${!!(token as any)?.refresh_token}.`, { module: 'DoctorPairingService' })),
				shareReplay(1)
			);
			this.cachedOAuthTokens.set(companyId, cachedToken$);
		} else {
			this.timberService.info(`[IOSim DoctorPairingService.getOAuthToken] Cache hit for companyId=${companyId}.`, { module: 'DoctorPairingService' });
		}
		
		return this.cachedOAuthTokens.get(companyId)!;
	}

	pairedStatus(): Observable<IdsPairingStatus> {
		this.timberService.info(`[IOSim DoctorPairingService.pairedStatus] Checking paired status.`, { module: 'DoctorPairingService' });
		let res = new IdsPairingStatus();
		return this.getIdsPairingData().pipe(
			mergeMap((idsPairingData) => {
				if (!idsPairingData || !idsPairingData.refreshToken) {
					this.timberService.info(`[IOSim DoctorPairingService.pairedStatus] No pairing data or refresh token — not paired. idsPairingData=${JSON.stringify(idsPairingData)}, refreshToken=${idsPairingData?.refreshToken}`, { module: 'DoctorPairingService' });
					res.isPaired = false;
					return of(res);
				} else {
					this.timberService.info(`[IOSim DoctorPairingService.pairedStatus] Pairing data found, refreshToken=${idsPairingData.refreshToken}. Checking OAuth token.`, { module: 'DoctorPairingService' });
					return this.getOAuthToken().pipe(
						mergeMap((oauth) => {
							if (!oauth || !oauth.refresh_token) {
								this.timberService.info(`[IOSim DoctorPairingService.pairedStatus] OAuth token missing refresh_token — not paired.`, { module: 'DoctorPairingService' });
								res.isPaired = false;
								return of(res);
							} else {
								this.timberService.info(`[IOSim DoctorPairingService.pairedStatus] OAuth token valid — paired.`, { module: 'DoctorPairingService' });
								res.isPaired = true;
								return of(res);
							}
						})
					);
				}
			})
		);
	}

	clearOAuthTokenCache(companyId?: number): void {
		if (companyId !== undefined) {
			this.cachedOAuthTokens.delete(companyId);
		} else {
			this.cachedOAuthTokens.clear();
		}
	}

	// I am not sure that it's necessary
	ngOnDestroy() {
		clearInterval(this.pollingInterval);
	}

	private setPollingInterval(loginWindow: Window) {
		this.pollingInterval = setInterval(() => {
			try {
				if (!loginWindow || (loginWindow && loginWindow.closed)) {
					// FIX: do not emit false here — the popup may momentarily close
					// during the OAuth redirect before the isSuccess callback arrives.
					// Emitting false here would be consumed by take(1) in checkPairedStatus
					// and block the launch even after a successful pairing.
					this.timberService.info(`[IOSim.setPollingInterval] Popup window is closed — stopping polling without emitting false.`, { module: 'DoctorPairingService' });
					clearInterval(this.pollingInterval);
				} else if (loginWindow.location.host === window.location.host) {
					const isAuthenticationSuccess =
						this.utils.getUrlParameterFromQueryString('isSuccess', loginWindow.location.search) === 'true';
					this.timberService.info(`[IOSim.setPollingInterval] Popup redirected back to host, isSuccess=${isAuthenticationSuccess}`, { module: 'DoctorPairingService' });
					if (isAuthenticationSuccess) {
						this.windowsApplicationService.setPromptLogin(!isAuthenticationSuccess);
					}
					this.iosimAuthenticationSuccess.next(isAuthenticationSuccess);
					this.timberService.info(`[IOSim.setPollingInterval] iosimAuthenticationSuccess emitted: ${isAuthenticationSuccess}`, { module: 'DoctorPairingService' });
					clearInterval(this.pollingInterval);
					loginWindow.close();
				} else {
					this.timberService.info(`[IOSim.setPollingInterval] Popup still open, waiting for redirect...`, { module: 'DoctorPairingService' });
				}
			} catch (e) {
				// Cross-origin access throws until the popup redirects back to our host — expected
				this.timberService.info(`[IOSim.setPollingInterval] Waiting for response from IDS login window (cross-origin).`, { module: 'DoctorPairingService' });
			}
		}, 1000);
	}
}
